// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.

/**
 * Registry of the operating guides that ship with the web app.
 *
 * This is the single source of truth for the available guides: the SuperAdmin
 * dropdown (OperatingGuideSettings) lists these, and OperatingGuide resolves the
 * account's selected id to the static HTML file rendered in the iframe. Adding a
 * guide is purely a frontend change — drop the HTML into
 * `public/operatingGuide/` and add an entry here.
 *
 * The backend only stores and validates the chosen `id`; an id not present here
 * falls back to DEFAULT_GUIDE_ID at render time.
 */
export interface OperatingGuideOption {
  /** Stable id persisted on the account (slug: [a-z0-9-]). */
  id: string;
  /** Human-readable label for the dropdown. */
  label: string;
  /** File name under public/operatingGuide/. */
  file: string;
}

export const OPERATING_GUIDES: OperatingGuideOption[] = [
  { id: 'mandown', label: 'Mandown Device', file: 'mandown-operating-guide.html' },
  { id: 'crew-device', label: 'Crew Device', file: 'crew-device-operating-guide.html' },
];

/** Used when the account has no selection or an unknown id is stored. */
export const DEFAULT_GUIDE_ID = 'mandown';

/** Resolve a guide id (possibly null/unknown) to its registry entry, never null. */
export const resolveGuide = (id: string | null | undefined): OperatingGuideOption => {
  const match = id ? OPERATING_GUIDES.find((g) => g.id === id) : undefined;
  return match ?? OPERATING_GUIDES.find((g) => g.id === DEFAULT_GUIDE_ID) ?? OPERATING_GUIDES[0];
};
